import { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import { useEvent } from '@legacy-providers/event';

import { api } from '@shared/api/api';
import { Label } from '@shared/forms/components/Label';
import { useZpl } from '@shared/labelary/hooks/useZpl';
import { Portlet } from '@shared/ui/components/Portlet';
import { Select } from '@shared/ui/components/Select';
import { useToast } from '@shared/ui/components/Toast';

type Props = RouteComponentProps<{ id: string }>;

export function EditWristbandTemplate({ match }: Props) {
  const { toast } = useToast();

  const event = useEvent();
  const periodId = Number(match.params.id);
  const [zpl, setZpl] = useState<string | null>(null);
  const [wristband, setWristband] = useState<any>('wristbandTemplateGeneral');
  const [copyFromOptions, setCopyFromOptions] = useState<any[]>([]);
  const [copyFrom, setCopyFrom] = useState<any>(null);
  const { renderZpl, imageUrl, loading, error } = useZpl(null);

  async function fetchCopyFromOptions() {
    const result = await api.get<any>('events?include=periods');

    const options: { label: string; value: number }[] = [];

    result.data.forEach((event: any) => {
      event.periods.forEach((period: any) => {
        options.push({
          label: event.name + ' - ' + period.name,
          value: period.id,
        });
      });
    });

    setCopyFromOptions(options);
  }

  async function loadZplFromPeriod(periodId: number): Promise<void> {
    const result = await api.get<any>('events/' + event.id + '/periods/' + periodId);
    const zplTemplate = result[wristband];

    setZpl(zplTemplate);
    renderZpl(zplTemplate);
  }

  async function updatePeriod() {
    let period = await api.get<any>('events/' + event.id + '/periods/' + periodId);
    period[wristband] = zpl;
    await api.put<any>('events/' + event.id + '/periods/' + periodId, period);

    toast({
      title: 'Success',
      description: 'Wristband updated.',
    });
  }

  useEffect(() => {
    loadZplFromPeriod(periodId);
    fetchCopyFromOptions();
  }, [periodId, wristband]);

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: error,
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  return (
    <>
      <Portlet className="mb-5 mt-5" innerClassName="flex items-center gap-8">
        <div>
          <Label>Wristband</Label>
          <Select
            className="min-w-[11ch]"
            options={[
              {
                label: 'General',
                value: 'wristbandTemplateGeneral',
              },
              {
                label: 'Artist',
                value: 'wristbandTemplateArtist',
              },
            ]}
            value={wristband}
            onChange={(value) => setWristband(value)}
          />
        </div>
        <div className="flex items-end space-x-4">
          <div>
            <Label>Copy From</Label>
            <Select
              className="min-w-[30ch]"
              options={copyFromOptions}
              value={wristband}
              onChange={(value) => setCopyFrom(value)}
            />
          </div>
          <div>
            <button className="btn btn-primary" onClick={() => copyFrom && loadZplFromPeriod(copyFrom)}>
              Copy
            </button>
          </div>
        </div>
      </Portlet>

      <Portlet>
        {loading && <div className="py-4">Loading preview...</div>}
        {imageUrl && <img className="w-[800px] border" src={imageUrl} alt="Rendered Wristband" />}
      </Portlet>

      <Portlet>
        <textarea
          className="form-control m-input"
          rows={100}
          value={zpl || ''}
          onChange={(e) => setZpl(e.target.value)}
        />
      </Portlet>

      <div className="sticky bottom-0 bg-white p-4 gap-4 flex border-t">
        <button
          className="btn btn-outline-primary"
          onClick={() => renderZpl(zpl)} // Update the preview with current textarea content
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Redraw'}
        </button>

        <button className="btn btn-primary" onClick={() => updatePeriod()}>
          Update
        </button>
      </div>
    </>
  );
}
